import type { MetricKind, Network, RefreshState } from '../api/types';

/**
 * The copy of the screen, apart from the components, for the same reason
 * `profileReadingCopy.ts` is: these sentences say what a number is and is not,
 * and reviewing them next to each other is what keeps them honest. A wording
 * change here is a claim change, not a style change.
 */

export const NETWORK_LABEL: Record<Network, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube'
};

export const NETWORK_PLACEHOLDER: Record<Network, string> = {
  instagram: 'instagram.com/rayrovisk',
  tiktok: 'tiktok.com/@rayrovisk',
  youtube: 'youtube.com/@rayrovisk'
};

export const NETWORK_INVALID: Record<Network, string> = {
  instagram: 'Isso não parece um perfil do Instagram.',
  tiktok: 'Isso não parece um perfil do TikTok.',
  youtube: 'Isso não parece um canal do YouTube.'
};

/**
 * Why a network has no numbers. Never the technical reason: `http_429` and
 * `relay_offline` are for the log, and on the screen they are the same sentence
 * because she can do the same thing about both — nothing, and wait.
 */
export const REFRESH_NOTE: Record<Exclude<RefreshState, 'ok'>, string> = {
  never: 'Ainda não lemos os números dessa rede.',
  not_found: 'Não encontramos esse perfil. Confira o link.',
  unavailable: 'Não conseguimos ler essa rede agora. Os outros números continuam valendo.',
  private: 'Esse perfil está privado, então os números não são públicos.'
};

export const METRIC_LABEL: Record<MetricKind, string> = {
  audience: 'Audiência',
  posts: 'Publicações',
  lifetimeViews: 'Views totais',
  averageViews: 'Média de views / post',
  engagementRate: 'Engajamento médio'
};

/**
 * What a network does not publish at all, which is a different sentence from
 * "não conseguimos ler agora": the first will never change, the second might,
 * and they send her to different places — one to nowhere, one to try later.
 */
export const UNSUPPORTED: Partial<Record<Network, Partial<Record<MetricKind, string>>>> = {
  instagram: { lifetimeViews: 'O Instagram não publica o total de views.' },
  tiktok: { lifetimeViews: 'O TikTok publica curtidas acumuladas, não views.' }
};

/** Her own setting, not ours and not the network's. It can change tomorrow. */
export const WITHHELD = 'Essa conta não mostra esse número.';

export const REFRESH_LABEL = 'Atualizar números';

export const REFRESH_QUEUED =
  'Estamos lendo as suas redes. Os números aparecem aqui em alguns instantes.';

/**
 * The refresh gave up waiting, which is not the same as the refresh failing.
 * The worker is still going; this screen simply stopped asking, and saying
 * "não conseguimos" would send her to retry work that is already running.
 */
export const REFRESH_STILL_RUNNING =
  'A leitura está demorando mais que o normal e continua rodando. Volte daqui a pouco.';

/** What came back, named per network. A silent stop reads as nothing happening. */
export function refreshOutcome(read: Network[], missed: Network[]): string {
  const names = (networks: Network[]) =>
    networks.map((network) => NETWORK_LABEL[network]).join(' e ');
  if (missed.length === 0) {
    return `Números atualizados: ${names(read)}.`;
  }
  if (read.length === 0) {
    return `Não conseguimos ler ${names(missed)} agora. Os números anteriores continuam valendo.`;
  }
  return `Atualizamos ${names(read)}. ${names(missed)} não respondeu, e os números anteriores continuam valendo.`;
}

export const PROVENANCE: Record<Network, string> = {
  instagram: 'O que está público no perfil',
  tiktok: 'O que está público no perfil',
  youtube: 'Dados da API do YouTube'
};

/**
 * What a connected account's numbers are, said differently — and the only
 * sentence on this screen allowed to call anything official (ADR-0007). It
 * replaces `PROVENANCE.instagram` for a snapshot whose `dataSource` is `meta`,
 * because those two are not the same claim about where a number came from.
 */
export const PROVENANCE_META = 'Dados oficiais da sua conta, enviados pelo Instagram';

export const EMPTY_TITLE = 'Adicione as suas redes';

export const EMPTY_DESCRIPTION =
  'Cole o link do seu perfil em cada rede. Depois disso o Roteirovisk lê o que já está público lá e junta os números aqui.';

/**
 * When the screen could not be read at all, which is a different sentence from
 * `EMPTY_TITLE` above and has to stay one: that one says "você ainda não
 * adicionou nenhuma rede", and printing it after a failed request tells her the
 * redes she saved are gone.
 */
export const ERROR_TITLE = 'Não conseguimos abrir os seus números';

export const ERROR_DESCRIPTION =
  'As suas redes continuam salvas. Foi a leitura desta tela que falhou — tente de novo em instantes.';

/**
 * The authorization card. Every sentence here is a claim about what Meta sends
 * once she approves, so it says what connecting buys and nothing beyond it.
 */
export const CONNECT_TITLE = 'Autorizar o Instagram';

export const CONNECT_DESCRIPTION =
  'Autorizando, o Instagram passa a mandar os números da sua conta profissional direto para cá — não só o que já está público no perfil. A autorização acontece no Instagram e nenhuma senha passa por aqui.';

export const CONNECT_CONNECTED =
  'A sua conta está autorizada. Os números oficiais entram nas próximas leituras.';

/**
 * The app is in development mode at Meta, so a button drawn as working would
 * send her to an error page Instagram wrote. Saying "em teste" is the honest
 * version of "you are not on a list you never knew existed".
 */
export const CONNECT_BLOCKED = 'Ainda em teste com um grupo pequeno. Em breve para todo mundo.';

export const CONNECT_LABEL = 'Autorizar o Instagram';

export const CONNECT_POPUP_BLOCKED =
  'O navegador bloqueou a janela do Instagram. Libere os pop-ups deste site e tente de novo.';
